import { useState } from 'react'
import { ArrowRight, Building2, Plus, Search, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { acceptInvitationByToken, sendJoinRequest } from '../hooks/useKanbifyData'
import { createCompany, findCompanyByCode } from '../hooks/useCompanyData'
import HorizontalScroller from './ui/HorizontalScroller'

export default function NoCompanyScreen({ onCompanyJoined }) {
    const { t } = useTranslation()
    const { user, refreshProfile, isAdmin } = useAuth()
    const initialInviteToken = new URLSearchParams(window.location.search).get('invite') || ''
    const [tab, setTab] = useState(initialInviteToken ? 'token' : (isAdmin ? 'create' : 'join'))
    const [code, setCode] = useState('')
    const [message, setMessage] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [inviteToken, setInviteToken] = useState(initialInviteToken)
    const [foundCompany, setFoundCompany] = useState(null)
    const [loading, setLoading] = useState(false)
    const [feedback, setFeedback] = useState('')
    const [sent, setSent] = useState(false)

    // feedbackTone heuristic: feedback is set from server messages or pre-translated strings.
    // After i18n we mark explicit error strings; default to error unless it's the success message.
    const feedbackTone = feedback && (feedback === t('noCompany.requestSent', { company: foundCompany?.name }))
        ? 'success'
        : feedback
            ? 'error'
            : 'success'

    const handleSearchCode = async (event) => {
        event.preventDefault()
        if (!code.trim()) return
        setLoading(true)
        setFoundCompany(null)
        setFeedback('')
        const company = await findCompanyByCode(code.trim())
        if (company) {
            setFoundCompany(company)
        } else {
            setFeedback(t('noCompany.codeNotFound'))
        }
        setLoading(false)
    }

    const handleSendRequest = async () => {
        if (!foundCompany) return
        setLoading(true)
        const { error } = await sendJoinRequest(foundCompany.id, user.id, message.trim())
        if (!error) {
            setSent(true)
            setFeedback(t('noCompany.requestSent', { company: foundCompany.name }))
        } else {
            setFeedback(error.message || t('noCompany.sendError'))
        }
        setLoading(false)
    }

    const handleCreateCompany = async (event) => {
        event.preventDefault()
        if (!companyName.trim()) return
        setLoading(true)
        const { data, error } = await createCompany(companyName.trim(), user.id)
        if (!error && data) {
            await refreshProfile()
            onCompanyJoined()
        } else {
            setFeedback(t('noCompany.createError', { error: error?.message || '' }))
        }
        setLoading(false)
    }

    const handleAcceptToken = async (event) => {
        event.preventDefault()
        if (!inviteToken.trim()) return
        setLoading(true)
        const { data, error } = await acceptInvitationByToken(inviteToken.trim(), user.id)
        if (data?.company_id) {
            await refreshProfile()
            window.history.replaceState({}, '', '/')
            onCompanyJoined()
        } else {
            setFeedback(data?.error || error?.message || t('noCompany.invalidInvite'))
        }
        setLoading(false)
    }

    const resetJoinFlow = () => {
        setFoundCompany(null)
        setSent(false)
        setCode('')
        setMessage('')
        setFeedback('')
    }

    return (
        <section className="no-company-screen">
            <div className="no-company-shell">
                <div className="no-company-hero">
                    <div className="empty-state no-company-hero-card">
                        <div className="empty-state-icon">
                            <Building2 size={60} style={{ color: 'var(--accent-blue)' }} />
                        </div>
                        <h2 className="empty-state-title">
                            {isAdmin ? t('noCompany.adminHeroTitle') : t('noCompany.employeeHeroTitle')}
                        </h2>
                        <p className="empty-state-text">
                            {isAdmin ? t('noCompany.adminHeroDesc') : t('noCompany.employeeHeroDesc')}
                        </p>
                    </div>

                    <div className="no-company-highlights">
                        <div className="no-company-highlight">
                            <strong>{t('noCompany.highlightBoardsTitle')}</strong>
                            <span>{t('noCompany.highlightBoardsDesc')}</span>
                        </div>
                        <div className="no-company-highlight">
                            <strong>{t('noCompany.highlightRolesTitle')}</strong>
                            <span>{t('noCompany.highlightRolesDesc')}</span>
                        </div>
                        <div className="no-company-highlight">
                            <strong>{t('noCompany.highlightOnboardingTitle')}</strong>
                            <span>{t('noCompany.highlightOnboardingDesc')}</span>
                        </div>
                    </div>
                </div>

                <div className="no-company-panel">
                    <HorizontalScroller
                        className="compact-scroller"
                        viewportClassName="no-company-tabs"
                        scrollStep={180}
                        label={t('noCompany.tabsLabel')}
                    >
                        {isAdmin ? (
                            <button type="button" className={`lb-tab ${tab === 'create' ? 'active' : ''}`.trim()} onClick={() => setTab('create')}>
                                <Plus size={14} /> {t('noCompany.tabCreate')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={`lb-tab ${tab === 'join' ? 'active' : ''}`.trim()}
                                onClick={() => {
                                    setTab('join')
                                    resetJoinFlow()
                                }}
                            >
                                <Search size={14} /> {t('noCompany.tabJoin')}
                            </button>
                        )}
                        <button type="button" className={`lb-tab ${tab === 'token' ? 'active' : ''}`.trim()} onClick={() => setTab('token')}>
                            <ArrowRight size={14} /> {t('noCompany.tabInvite')}
                        </button>
                    </HorizontalScroller>

                    {feedback ? (
                        <div className={`no-company-feedback ${feedbackTone}`.trim()}>
                            {feedback}
                        </div>
                    ) : null}

                    {tab === 'create' && isAdmin ? (
                        <form onSubmit={handleCreateCompany} className="no-company-form-card">
                            <div className="form-group">
                                <label className="form-label"><Building2 size={13} /> {t('noCompany.companyNameLabel')}</label>
                                <input
                                    value={companyName}
                                    onChange={(event) => setCompanyName(event.target.value)}
                                    placeholder={t('noCompany.companyNamePlaceholder')}
                                    autoFocus
                                    required
                                />
                            </div>

                            <button type="submit" className="btn btn-primary no-company-submit" disabled={loading}>
                                {loading ? t('noCompany.creating') : t('noCompany.createCompany')}
                            </button>
                        </form>
                    ) : null}

                    {tab === 'join' && !isAdmin ? (
                        <div className="no-company-join-flow">
                            {!foundCompany ? (
                                <form onSubmit={handleSearchCode} className="no-company-form-card">
                                    <div className="form-group">
                                        <label className="form-label"><Search size={13} /> {t('noCompany.joinCodeLabel')}</label>
                                        <input
                                            value={code}
                                            onChange={(event) => setCode(event.target.value.toUpperCase())}
                                            placeholder={t('noCompany.joinCodePlaceholder')}
                                            className="no-company-code-input"
                                            maxLength={8}
                                            autoFocus
                                            required
                                        />
                                        <div className="no-company-helper">
                                            {t('noCompany.joinCodeHelper')}
                                        </div>
                                    </div>

                                    <button type="submit" className="btn btn-primary no-company-submit" disabled={loading}>
                                        <Search size={14} /> {loading ? t('noCompany.searching') : t('noCompany.searchCompany')}
                                    </button>
                                </form>
                            ) : !sent ? (
                                <div className="no-company-form-card">
                                    <div className="no-company-found-card">
                                        <span className="no-company-found-label">{t('noCompany.foundCompany')}</span>
                                        <strong>{foundCompany.name}</strong>
                                        <span>{t('noCompany.codeLabel', { code: foundCompany.join_code })}</span>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">{t('noCompany.messageLabel')}</label>
                                        <textarea
                                            value={message}
                                            onChange={(event) => setMessage(event.target.value)}
                                            rows={3}
                                            placeholder={t('noCompany.messagePlaceholder')}
                                        />
                                    </div>

                                    <div className="no-company-actions">
                                        <button type="button" className="btn btn-primary" onClick={handleSendRequest} disabled={loading}>
                                            <Send size={14} /> {loading ? t('noCompany.sending') : t('noCompany.sendRequest')}
                                        </button>
                                        <button type="button" className="btn btn-secondary" onClick={resetJoinFlow}>
                                            {t('common.back')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-state no-company-success-card">
                                    <div className="empty-state-icon">
                                        <Send size={42} style={{ color: 'var(--accent-green)' }} />
                                    </div>
                                    <h3 className="empty-state-title">{t('noCompany.requestSentTitle')}</h3>
                                    <p className="empty-state-text">
                                        {t('noCompany.requestSentDesc')}
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : null}

                    {tab === 'token' ? (
                        <form onSubmit={handleAcceptToken} className="no-company-form-card">
                            <div className="form-group">
                                <label className="form-label">{t('noCompany.tokenLabel')}</label>
                                <input
                                    value={inviteToken}
                                    onChange={(event) => setInviteToken(event.target.value)}
                                    placeholder={t('noCompany.tokenPlaceholder')}
                                    autoFocus
                                    required
                                />
                                <div className="no-company-helper">
                                    {t('noCompany.tokenHelper')}
                                </div>
                            </div>

                            <button type="submit" className="btn btn-primary no-company-submit" disabled={loading}>
                                <ArrowRight size={14} /> {loading ? t('noCompany.verifying') : t('noCompany.acceptInvite')}
                            </button>
                        </form>
                    ) : null}
                </div>
            </div>
        </section>
    )
}
